"use client";

// Persistent floating chat entry on every page (Play 3 of the AI Astrologer
// prominence audit). Hidden on /free-kundali/ (and /hi/free-kundali/),
// /ai-astrologer/, /free-astrology-chat/ where InlineChatPreview /
// KundaliResult.ChatPanel already render the chat inline — a sticky pill on top
// of those would be redundant noise.
//
// Mounted once, by the root layout. The Hindi article templates rely on it and
// must not render a second copy (StickyChatButton.test.tsx pins the one mount).

import Link from "next/link";
import { usePathname } from "next/navigation";
import { useEffect, useRef, useState, type RefObject } from "react";

import { shouldShowStickyChatButton } from "@/lib/global-overlays";
import { safeSessionSet } from "@/lib/browser-storage";
import { FLOATING_CHAT_PILL_CLEARANCE_PX } from "@/lib/sticky-chrome-offsets";

// One literal, so tests/mobile-fixed-bottom-layers.test.ts can read the layer.
const PILL_CLASS =
  "fixed bottom-[76px] right-4 z-40 flex min-h-11 items-center gap-2 rounded-full bg-surface-brand-strong px-4 py-3 text-body-small font-semibold text-text-on-brand shadow-card transition hover:scale-105 md:bottom-6 max-md:[body:has([data-floating-card])_&]:hidden";
// Out of sight and out of the tap path, but still laid out: its box is what
// "under the pill" is measured against, and GlobalAgentEntry keeps deferring to
// the [data-agent-bridge] node instead of popping a card over the same form.
const STEP_ASIDE_CLASS = "pointer-events-none invisible opacity-0";

// Inputs that open a keyboard or picker. On phones the keyboard lifts the
// bottom-anchored pill onto the field being typed in (/kundli-matching date).
const NON_TEXT_INPUT_TYPES = new Set([
  "button",
  "checkbox",
  "color",
  "file",
  "hidden",
  "image",
  "radio",
  "range",
  "reset",
  "submit",
]);
// A modal (the header search) already covers the pill; the pill stays put.
const MODAL_SELECTOR = "[role='dialog'], [aria-modal='true'], dialog";
// The page's own forms: the pill steps aside while any of them passes under it
// (the /panchang "Show" button). Rescanned on DOM changes, since tool forms
// mount late (lazy islands, the second kundli-matching step).
const PAGE_FORM_SELECTOR = "#main-content form";

function isTextEntry(target: EventTarget | null): boolean {
  if (!(target instanceof Element) || target.closest(MODAL_SELECTOR)) return false;
  if (target instanceof HTMLTextAreaElement || target instanceof HTMLSelectElement) return true;
  if (target instanceof HTMLInputElement) return !NON_TEXT_INPUT_TYPES.has(target.type);
  return target instanceof HTMLElement && target.isContentEditable;
}

/**
 * True while the pill would cover what the visitor is filling in:
 *  - focus sits in a field that takes typing or a picker;
 *  - one of the page's forms overlaps the pill's own box. An
 *    IntersectionObserver whose root is shrunk to that box: no scroll handler,
 *    no layout reads while scrolling. Rebuilt when the box can move or appear
 *    (a resize across md, the floating-card rule showing it again).
 * A rAF-batched MutationObserver rescan picks up forms that mount late and
 * re-reads focus: Safari and Firefox fire no focusout when the focused field
 * is removed (a step of a multi-step form), which would leave the pill hidden.
 */
function useStepAside(pill: RefObject<HTMLElement | null>, active: boolean): boolean {
  const [typing, setTyping] = useState(false);
  const [underPill, setUnderPill] = useState(false);
  useEffect(() => {
    if (!active) return;
    const onFocusIn = (event: FocusEvent) => setTyping(isTextEntry(event.target));
    // relatedTarget is where focus goes next (null when it leaves the page).
    const onFocusOut = (event: FocusEvent) => setTyping(isTextEntry(event.relatedTarget));
    document.addEventListener("focusin", onFocusIn);
    document.addEventListener("focusout", onFocusOut);

    const element = pill.current;
    const overlapping = new Set<Element>();
    let observer: IntersectionObserver | null = null;
    let frame = 0;

    const publish = () => {
      for (const form of overlapping) if (!form.isConnected) overlapping.delete(form);
      setUnderPill(overlapping.size > 0);
    };
    const observeForms = () => {
      if (!observer) return;
      for (const form of document.querySelectorAll(PAGE_FORM_SELECTOR)) observer.observe(form);
    };
    const build = () => {
      observer?.disconnect();
      observer = null;
      overlapping.clear();
      const box = element?.getBoundingClientRect();
      // display:none (a floating card is up below md): nothing to cover.
      if (
        !box ||
        box.width === 0 ||
        box.height === 0 ||
        typeof IntersectionObserver === "undefined"
      ) {
        publish();
        return;
      }
      const viewportWidth = document.documentElement.clientWidth || window.innerWidth;
      const inset = (distance: number) =>
        `${-Math.max(0, Math.round(distance - FLOATING_CHAT_PILL_CLEARANCE_PX))}px`;
      const rootMargin = [
        inset(box.top),
        inset(viewportWidth - box.right),
        inset(window.innerHeight - box.bottom),
        inset(box.left),
      ].join(" ");
      observer = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (entry.isIntersecting) overlapping.add(entry.target);
            else overlapping.delete(entry.target);
          }
          publish();
        },
        { rootMargin }
      );
      observeForms();
      publish();
    };
    const rescan = () => {
      setTyping(isTextEntry(document.activeElement));
      observeForms();
      publish();
    };
    // One frame for both jobs. A pending rebuild survives any later rescan
    // request (and the reverse): a DOM change right after the pill reappears
    // must not drop the rebuild and leave forms unwatched.
    let needsBuild = false;
    let pending = false;
    const flush = () => {
      pending = false;
      if (needsBuild) {
        needsBuild = false;
        build();
      }
      rescan();
    };
    const schedule = (rebuild: boolean) => {
      if (rebuild) needsBuild = true;
      if (pending) return;
      pending = true;
      frame = requestAnimationFrame(flush);
    };
    const onResize = () => schedule(true);

    build();
    const mutations =
      typeof MutationObserver === "undefined" ? null : new MutationObserver(() => schedule(false));
    mutations?.observe(document.body, { childList: true, subtree: true });
    const resizes =
      typeof ResizeObserver === "undefined" || !element
        ? null
        : new ResizeObserver(() => schedule(true));
    if (element) resizes?.observe(element);
    window.addEventListener("resize", onResize);
    return () => {
      document.removeEventListener("focusin", onFocusIn);
      document.removeEventListener("focusout", onFocusOut);
      cancelAnimationFrame(frame);
      observer?.disconnect();
      mutations?.disconnect();
      resizes?.disconnect();
      window.removeEventListener("resize", onResize);
      setTyping(false);
      setUnderPill(false);
    };
  }, [active, pill]);
  return typing || underPill;
}

export default function StickyChatButton() {
  const pathname = usePathname();
  const visible = shouldShowStickyChatButton(pathname);
  const pillRef = useRef<HTMLAnchorElement>(null);
  // It never covers what the visitor is filling in: it steps aside while a
  // field has focus and while a form of the page passes under it.
  const stepAside = useStepAside(pillRef, visible);
  if (!visible) return null;
  const isHindi = pathname === "/hi" || pathname?.startsWith("/hi/");
  // Hindi must land on a chat-first surface, mirroring the English destination.
  // /hi/astrologers/ is a persona DIRECTORY whose cards are inert while
  // APP_AVAILABLE=false — routing the global chat CTA there was a dead end
  // (hi first_msg 6.7% vs en 29.8%, 30d to 2026-08-13).
  const chatHref = isHindi ? "/hi/muft-jyotish-chat/" : "/ai-astrologer/";
  // One label at every width, and it is the accessible name (WCAG 2.5.3): the
  // pill used to show "Ask Pandit-ji" while screen readers heard "Ask the AI
  // Jyotishi", and Hindi pages got romanized Hinglish (c-a11y-10).
  const label = isHindi ? "AI ज्योतिषी से पूछें" : "Ask AI Jyotishi";

  function handleClick() {
    try {
      safeSessionSet("seo_entry_path", pathname || "/");
    } catch {
      // Best-effort context handoff.
    }
  }

  return (
    <Link
      ref={pillRef}
      href={chatHref}
      prefetch={false}
      onClick={handleClick}
      lang={isHindi ? "hi" : "en"}
      // GlobalAgentEntry defers to any visible [data-agent-bridge], so the pill
      // and the contextual card never stack (C15). md, not sm: the bottom nav is
      // md:hidden, so at 640–767px a sm:bottom-6 pill sat under it. Below md it
      // also steps aside while a floating card (push prompt, agent card) is up,
      // instead of sitting untappable underneath it.
      data-agent-bridge="sticky"
      data-step-aside={stepAside ? "true" : undefined}
      className={stepAside ? `${PILL_CLASS} ${STEP_ASIDE_CLASS}` : PILL_CLASS}
    >
      <span aria-hidden className="text-body">
        ॐ
      </span>
      <span>{label}</span>
    </Link>
  );
}
